import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { Event } from '@interfaces';
import { DialogService, DroplistItem, DroplistWidget } from '@eforall/dashboard';
import { DomainDataService } from '../../../../../../services';
import { AcceleratorAreaService } from '../../../../accelerator/accelerator-area.service';
import { CommentsDialog } from './dialog-comments/comments.dialog';


@Component({
	selector: 'acc-delivery-curriculum-tab-part',
	standalone: true,
	imports: [
		DroplistWidget,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './acc-delivery-curriculum-tab.part.html',
})
export class AccDeliveryCurriculumTabPart {

	private readonly areaService = inject(AcceleratorAreaService);
	private readonly util = inject(UtilityService);
	private readonly domain = inject(DomainDataService);
	private readonly dialogService = inject(DialogService);

	public sessionsWithSurveys = computed<DroplistItem<{ hasSurveys: boolean, event: Event | undefined }>[]>(() => {
		const data = this.areaService.data();
		if (!data) return [];

		return data.sessions.reduce((a: DroplistItem<{ hasSurveys: boolean, event: Event | undefined }>[], session) => {
			const event = data.events.find(e => e.eventId === session.eventId);
			a.push({
				uniqueId: session.accSessionId,
				groupName: 'Sessions',
				iconContext: {
					iconClass: 'fa-square-t',
					iconStyle: 'fa-regular',
					text: event ? this.util.date.formatUTC(event.startUTC, 'MMM D, YYYY (DOW)', 'H:MM AM EST', 'en-US') : '',
					hideText: 'never',
				},
				data: {
					hasSurveys: data.sessionSurveyResponses.some(ssr => ssr.eventId === session.eventId),
					event,
				},
			});

			return a;
		}, []);
	});
	public selectedViewItem = signal<DroplistItem<{ hasSurveys: boolean, event: Event | undefined }> | undefined>(
		this.getActiveOrLastSession()
	);


	public surveyCountsForAllSessions = computed<{
		accSessionId: number;
		attendeesPresent: number;
		surveysComplete: number;
		session: {
			accessibilityScore: number;
			communicationScore: number;
			organizationScore: number;
			commentsCount: number;
			sessionName: string,
			comments: {
				comment: string,
				name: string
			}[],
		};
		topics: {
			topicName: string;
			relevanceScore: number;
			needScore: number;
			understandingScore: number;
			durationScore: number;
			commentsCount: number;
			comments: {
				comment: string,
				name: string
			}[],
			specialists: {
				specialistName: string;
				knowledgeScore: number;
				effectivenessScore: number;
				professionalismScore: number;
				commentsCount: number;
				comments: {
					comment: string,
					name: string
				}[],
			}[];
		}[];
	}[]>(() => {
		const data = this.areaService.data();
		const selectedItem = this.selectedViewItem();
		if (!data || !selectedItem) return [];

		return data.sessions.map(session => {

			const accSessionAttendeeMap = this.util.array.toMap(data.sessionAttendees, (a) => a.accSessionAttendeeId);
			const accSessionSurveyResponseMap = this.util.array.toMap(data.sessionSurveyResponses, (a) => a.accSessionSurveyResponseId);
			const attendeesPresent = data.sessionAttendees.filter(a => a.accSessionId === session.accSessionId).map(a => a.status == 'Present').length;
			const sessionSurveys = data.sessionSurveyResponses.filter(ssr => ssr.eventId === session.eventId);

			const sessionTopics = data.sessionTopics.filter(st => st.accSessionId === session.accSessionId);
			const topicSurveys = data.topicSurveyResponses.filter(stsr => sessionSurveys.map(s => s.accSessionSurveyResponseId).includes(stsr.accSessionSurveyResponseId));
			const specialistSurveys = data.specialistSurveyResponses.filter(sssr => topicSurveys.map(ts => ts.accSessionTopicSurveyResponseId).includes(sssr.accSessionTopicSurveyResponseId));

			const sessionComments = sessionSurveys.filter(session => !!session.other);
			const event = data.events.find(e => e.eventId === session.eventId);

			const sessionName = event ? this.util.date.formatUTC(event.startUTC, 'MMM D, YYYY (DOW)', 'H:MM AM EST', 'en-US') : '';

			return {
				accSessionId: session.accSessionId,
				attendeesPresent,
				surveysComplete: sessionSurveys.length,
				session: {
					accessibilityScore: sessionSurveys.reduce((a, s) => a + (s.accessibility || 0), 0),
					communicationScore: sessionSurveys.reduce((a, s) => a + (s.communication || 0), 0),
					organizationScore: sessionSurveys.reduce((a, s) => a + (s.organization || 0), 0),
					commentsCount: sessionComments.length,
					sessionName,
					comments: sessionComments.map(s => ({
						comment: s.other!,
						name: accSessionAttendeeMap[s.accSessionAttendeeId]!.personName,
					})),

				},
				topics: sessionTopics.reduce((a: {
					topicName: string;
					relevanceScore: number;
					needScore: number;
					understandingScore: number;
					durationScore: number;
					commentsCount: number;
					comments: {
						comment: string,
						name: string
					}[],
					specialists: {
						specialistName: string;
						knowledgeScore: number;
						effectivenessScore: number;
						professionalismScore: number;
						commentsCount: number;
						comments: {
							comment: string,
							name: string
						}[],
					}[];
				}[], topic) => {
					const topicId = data.accTopics.find(at => at.accTopicId === topic.accTopicId)!.topicId;

					const topicsMap = this.domain.data().topicsMap;

					const topicSurveysResponses = topicSurveys.filter(ts => ts.topicId === topicId);

					if (!topicSurveysResponses.length) return a;

					const specialists = data.topicSpecialists.filter(ts => ts.accTopicId === topic.accTopicId);

					const topicComments = topicSurveysResponses.filter(topic => !!topic.other);

					a.push({
						topicName: topicsMap[topicId]!.name,
						relevanceScore: topicSurveysResponses.reduce((a, s) => a + (s.relevance || 0), 0),
						needScore: topicSurveysResponses.reduce((a, s) => a + (s.need || 0), 0),
						understandingScore: topicSurveysResponses.reduce((a, s) => a + (s.understanding || 0), 0),
						durationScore: topicSurveysResponses.reduce((a, s) => a + (s.duration || 0), 0),
						commentsCount: topicComments.length,
						comments: topicComments.map(s => ({
							comment: s.other!,
							name: accSessionAttendeeMap[accSessionSurveyResponseMap[s.accSessionSurveyResponseId]!.accSessionAttendeeId]!.personName,
						})),
						specialists: specialists.map(specialist => {
							const specialistSurveysForPerson = specialistSurveys.filter(sss => sss.personId == specialist.personId);
							const specialistComments = specialistSurveysForPerson.filter(sp => !!sp.other);

							return {
								specialistName: specialist.personName,
								knowledgeScore: specialistSurveysForPerson.reduce((a, s) => a + (s.knowledge || 0), 0),
								effectivenessScore: specialistSurveysForPerson.reduce((a, s) => a + (s.effectiveness || 0), 0),
								professionalismScore: specialistSurveysForPerson.reduce((a, s) => a + (s.professionalism || 0), 0),
								commentsCount: specialistComments.length,
								comments: specialistComments.map(s => {
									const topicResponse = topicSurveysResponses.find(t => t.accSessionTopicSurveyResponseId == s.accSessionTopicSurveyResponseId)!;
									return {
										comment: s.other!,
										name: accSessionAttendeeMap[accSessionSurveyResponseMap[topicResponse.accSessionSurveyResponseId]!.accSessionAttendeeId]!.personName,
									}
								}),
							}
						}),
					});
					return a;
				}, []),
			}
		});
	})


	public readonly surveyCount = computed(() => {
		const selectedItem = this.selectedViewItem();
		if (!selectedItem || (selectedItem && !selectedItem.data?.hasSurveys)) return undefined;

		const surveyCounts = this.surveyCountsForAllSessions().find(s => s.accSessionId === selectedItem.uniqueId);
		return surveyCounts;
	});


	public onDroplistChange(item: DroplistItem<{ hasSurveys: boolean, event: Event | undefined }> | undefined) {
		this.selectedViewItem.set(item);
	}


	private getActiveOrLastSession(): DroplistItem<{ hasSurveys: boolean, event: Event | undefined }> | undefined {
		const sessions = this.sessionsWithSurveys();
		if (!sessions.length) return undefined;

		const now = Date.now() / 1000; // current UTC in seconds

		// Find the session whose event is currently active
		const activeSession = sessions.find(item => {
			const event = item.data?.event;
			return event && event.startUTC <= now && now <= event.toolEndUTC;
		});

		if (activeSession) return activeSession;

		// If no active session, and current time is before first session, return first session
		if (now < (sessions[0]?.data?.event?.startUTC || 0)) return sessions[0];

		// otherwise the last session
		return sessions[sessions.length - 1];
	}

	public async showDialog(comments: { name: string, comment: string }[], label: string) {
		const data: { comments: { name: string, comment: string }[], label: string } = {
			comments,
			label
		};

		await this.dialogService.showCustom(CommentsDialog, { data }, 990, 550);

	}


}
